import {
  catcherMittCatchFaceObjs,
  catchFacePartsObjs,
  gloveBeltBackPartsForBomberSeriesObjs,
  gloveBeltBackPartsObjs,
  catcherMittBackOfBeltBackStylePartsObjs,
  firstMittBackPartsObjs,
  dispatchForBomberSeriesObjects,
  dispatchForNormalSeriesObjects,
  dispatchForCatcherObjects,
  gorillaLabelObjs,
  gloveBeltBackPartsForBomber001Objs,
  gorillaBomberLabelObjs,
  laceColorObjs,
  gloveBeltBackPartsForBomberHudObjs,
  dispatchForBomberHudObjects,
  dispatchForBomberBknObjects,
  gloveBeltBackPartsForBomberBknObjs
} from 'src/features/brands/gorilla/constants/color'
import { Position, State, StateObj } from 'src/types'
import { positionChecker } from 'src/util/useModelDesign'

export const genColorObjects = (specifiedPosition: Position, state: State) => {
  const {
    all,
    catchFace,
    lace,
    web,
    web2,
    edge,
    stitch,
    hamidashi,
    thumbHook,
    littleHook,
    indexCover,
    linings,
    mouton,
    gorillaLabel,
    catchFaceTarget,
    fingerStand,
    thumbIndexMiddleRight, // 親指・人差指・中指1
    listBelt,
    littleOut, // 小指左
    middleLeftRingLittle, // 中指2・薬指・小指
    // middleLeftRing, // 中指2・薬指
    // thumbWebAndIndexWeb, // UBackで使用
    thumb,
    underWeb,
    boomerang,
    series
  } = state
  const commonParts = {
    all,
    catchFace,
    lace,
    web,
    web2,
    edge,
    stitch,
    hamidashi,
    thumbHook,
    littleHook,
    indexCover,
    linings,
    gorillaLabel
  }
  switch (specifiedPosition) {
    case 'catcher':
      return {
        ...commonParts,
        mouton,
        catchFaceTarget,
        fingerStand,
        thumbIndexMiddleRight, // 親指・人差指・中指1
        middleLeftRingLittle // 中指2・薬指・小指
      }
    case 'first':
      return {
        ...commonParts,
        mouton,
        listBelt,
        thumb,
        underWeb,
        boomerang
      }
    default:
      switch (series?.value) {
        case 'bomber':
          // BOMBERシリーズはムートンなし
          return {
            ...commonParts,
            listBelt,
            thumbOut: state?.thumbOut,
            thumbWeb: state?.thumbWeb,
            indexWeb: state?.indexWeb,
            indexMiddle: state?.indexMiddle,
            middleIndex: state?.middleIndex,
            middleRing: state?.middleRing,
            ringMiddle: state?.ringMiddle,
            ringLittle: state?.ringLittle,
            littleRing: state?.littleRing,
            littleOut,
            hudLeft: state?.hudLeft,
            hudRight: state?.hudRight
          }
        default:
          return {
            ...commonParts,
            mouton,
            listBelt,
            thumbOut: state?.thumbOut,
            thumbWeb: state?.thumbWeb,
            indexWeb: state?.indexWeb,
            indexMiddle: state?.indexMiddle,
            middleIndex: state?.middleIndex,
            middleRing: state?.middleRing,
            ringMiddle: state?.ringMiddle,
            ringLittle: state?.ringLittle,
            littleRing: state?.littleRing,
            littleOut,
            bknLeft: state?.bknLeft,
            bknRight: state?.bknRight
          }
      }
  }
}

const selectFrontPartsObject = (specifiedPosition: Position) => {
  switch (specifiedPosition) {
    case 'catcher':
      return catcherMittCatchFaceObjs
    case 'first':
      return catchFacePartsObjs
    default:
      return catchFacePartsObjs
  }
}

const selectBackPartsObject = (specifiedPosition: Position, isBomberSeries: boolean, is001Model: boolean, isHudOrBknModel) => {
  switch (specifiedPosition) {
    case 'pitcher':
      if (isBomberSeries) {
        if (is001Model) return gloveBeltBackPartsForBomber001Objs
        if (isHudOrBknModel) return gloveBeltBackPartsForBomberHudObjs
        return gloveBeltBackPartsForBomberSeriesObjs
      }
      return gloveBeltBackPartsObjs
    case 'infielder':
      return gloveBeltBackPartsObjs
    case 'outfielder':
      if (isHudOrBknModel) return gloveBeltBackPartsForBomberBknObjs
      return gloveBeltBackPartsObjs
    case 'catcher':
      return catcherMittBackOfBeltBackStylePartsObjs
    case 'first':
      return firstMittBackPartsObjs
  }
}

export const partsGenerator = (position: Position, series: StateObj, partsKey: string, figurePanelNum: number, productNumber: string): StateObj[] => {
  const { isBomberSeries, is001Model, isHudModel, isBknModel, isPitcher } = gorillaColorFlagGenerator(position, series, partsKey, productNumber)
  const isFrontFigurePane = figurePanelNum === 0
  const defaultFront = selectFrontPartsObject(position)
  const frontParts = isPitcher ? defaultFront.filter((p) => p.value !== 'web2') : defaultFront
  const isHudOrBknModel = isHudModel || isBknModel
  const backParts = selectBackPartsObject(position, isBomberSeries, is001Model, isHudOrBknModel)
  return isFrontFigurePane ? frontParts : backParts
}

export const gorillaColorFlagGenerator = (position: Position, series: StateObj, partsKey: string, productNumber = '') => {
  const { isPitcher, isCatcher } = positionChecker(position)
  const isCatcherHamidashi = isCatcher && partsKey === 'hamidashi'
  const isBomberSeries = series?.value === 'bomber' && isPitcher
  const isSelectable = series?.value !== 'unselected' || !isPitcher
  const isLaceParts = partsKey === 'lace'
  const isLabelParts = partsKey === 'gorillaLabel'
  const is001Model = productNumber === '001'
  const isHudModel = productNumber === 'HUD'
  const isBknModel = productNumber === 'BKN'
  return { isBomberSeries, isPitcher, isCatcherHamidashi, isSelectable, isLaceParts, isLabelParts, is001Model, isHudModel, isBknModel }
}

export const dispatchColorObjectsGenerator = (position: Position, series: StateObj, partsKey: string, productNumber = ''): StateObj[] => {
  const { isBomberSeries, isCatcherHamidashi, isHudModel, isBknModel } = gorillaColorFlagGenerator(position, series, partsKey, productNumber)
  const dispatchObjects = isBomberSeries ? dispatchForBomberSeriesObjects : dispatchForNormalSeriesObjects
  const dispatchObjects2 = isCatcherHamidashi ? dispatchForCatcherObjects : dispatchObjects
  const dispatchObjects3 = isHudModel ? dispatchForBomberHudObjects : dispatchObjects2
  const dispatchObjects4 = isBknModel ? dispatchForBomberBknObjects : dispatchObjects3
  return dispatchObjects4[partsKey]
}

export const colorCautionGenerator = (condition: boolean, key: string): string => {
  if (!condition) return ''
  switch (key) {
    case 'lace':
      return '中白系カラーは断面が白色です。シュミレーションには反映されません。'
  }
}

export const gorillaLabelCautionGenerator = (condition: boolean, labelPartsValue: string, position: Position): string => {
  if (!condition) return ''
  switch (labelPartsValue) {
    case 'overAllEmbroidery':
      switch (position) {
        case 'pitcher':
          return 'ホワイトが含まれるため、公式戦(投手用)ではご利用いただけません。直刺繍ラベルはオプション料金が発生します。'
        default:
          return '直刺繍ラベルはオプション料金が発生します。'
      }
    case 'headEmbroidery':
      return '直刺繍ラベルはオプション料金が発生します。'
    default:
      return ''
  }
}

export const labelObjectsSwitcher = (position: Position, series: string): StateObj[] => {
  switch (position) {
    case 'pitcher':
      switch (series) {
        case 'bomber':
          return gorillaBomberLabelObjs
        default:
          return gorillaLabelObjs
      }
    default:
      return gorillaLabelObjs
  }
}
